import * as Sentry from "@sentry/react";
import React, { Component, ErrorInfo, ReactNode } from "react";
import { RefreshCw } from "lucide-react";
import { Logo } from './Logo';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
    Sentry.captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
    });
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-paper p-10 md:p-16">
          <div className="max-w-md">
            {/* Editorial header — Logo mark + section label */}
            <div className="flex items-center gap-3 mb-10 pb-5 border-b border-ink-200">
              <Logo variant="mark" size={26} />
              <span className="text-[10px] font-medium uppercase tracking-widest text-danger-600">System Error</span>
            </div>

            <h1 className="text-3xl font-serif font-medium text-ink mb-4">Something went wrong</h1>
            <p className="text-ink-500 leading-relaxed mb-10 text-sm">
              The Oracle encountered an unexpected error. This might be due to corrupted local data or a temporary service issue.
            </p>

            <div className="space-y-3">
              <button
                onClick={() => window.location.reload()}
                className="w-full py-3 bg-oxblood hover:bg-oxblood-800 text-paper text-[10px] font-medium uppercase tracking-[0.3em] rounded-md transition-all flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Reload Application</span>
              </button>
              <button
                onClick={() => {
                  localStorage.clear();
                  window.location.reload();
                }}
                className="w-full py-3 bg-surface hover:bg-surface-2 text-ink border border-ink-200 text-[10px] font-medium uppercase tracking-[0.3em] rounded-md transition-all"
              >
                Clear Cache & Reset
              </button>
            </div>

            {this.state.error && (
              <p className="mt-10 text-[8px] text-ink-500 font-mono break-all opacity-50">
                {this.state.error.toString()}
              </p>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
